import _ from 'underscore';
import { DeterministicView, helpers } from '@bingads-webui/deterministic-view';
import AlertView from '../alert/index';
import template from './template.pug';

const { updateView, ensureChildView } = helpers;

/**
 * @typedef {Object} AlertListViewState
 * @property {string[]} alertIds - A list of alert ids
 * @property {Object.<string, Alert>} alerts - A mapping from ids to alert models
 */

/**
 * Represents a list of alerts, plus an editor to add/change alerts
 */
export default class AlertListView extends DeterministicView {
  initialize({
    level,
  }) {
    this.$el.attr({
      'data-level': level,
    });
    this.setState();
  }

  /**
   * Propagate the state to model and children views.
   * @param {AlertListViewState} state - The new state
   * @override
   * @returns {AlertListView} - The view itself
   */
  setState({ alertIds = [], alerts = {} } = {}) {
    _.each(alertIds, (alertId) => {
      ensureChildView(this, `alert(${alertId})`, AlertView, {}, {
        ignore: () => {
          this.trigger('ignore', {
            id: alertId,
            alert: alerts[alertId],
          });
          this.dismiss(alertId);
        },
      }).setState(alerts[alertId]);
    });

    return super.setState({
      alertIds,
    });
  }

  /**
   * Collect the state from model and children views.
   * @override
   * @returns {AlertListViewState} - The state of the view.
   */
  getState() {
    const { alertIds = [] } = super.getState();

    return {
      alertIds,
      alerts: _.object(alertIds, _.map(alertIds, key => this.getChild(`alert(${key})`).getState())),
    };
  }

  static newId() {
    return _.uniqueId('alert-');
  }

  // TODO: [#591704] after Services/AlertService is refactored, ID needs validation to make
  // sure it's generated by this component to avoid collision
  show(alert, id) {
    const {
      alertIds,
      alerts,
    } = this.getState();

    this.setState({
      // keep tracking the alert show order by time through the alert Id
      alertIds: _.union(alertIds, [id]),
      alerts: _.defaults({
        [id]: alert,
      }, alerts),
    });

    return id;
  }

  dismiss(alertId) {
    const {
      alertIds,
    } = this.getState();

    updateView(this, {
      alertIds: _.without(alertIds, alertId),
    });
  }

  dismissAll() {
    const {
      alertIds,
    } = this.getState();

    _.each(alertIds, id => this.dismiss(id));
  }

  get className() {
    return 'alert-center__level';
  }

  get attributes() {
    return {
      role: 'group',
    };
  }

  get tagName() {
    return 'ol';
  }

  get template() {
    return template;
  }
}
